import { useEffect, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";

// Detects a fresh deploy two ways:
// 1. /version.json changes (stamped at build time by scripts/gen-version.js)
// 2. the hashed main bundle in "/" no longer matches the one we're running
// Shows a can't-miss banner so field crews refresh instead of hitting stale-code bugs.

const fetchVersion = async () => {
  const r = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
  if (!r.ok) throw new Error("no version file");
  return r.json();
};

const currentBundle = () =>
  Array.from(document.scripts).map((s) => s.src).find((s) => /\/static\/js\/main\.[a-z0-9]+\.js/.test(s));

export function AppUpdateBanner() {
  const [update, setUpdate] = useState(null); // { version } once a new deploy is detected
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let initialVersion = null;
    let stop = false;
    let last = 0;
    const bundle = currentBundle();

    const check = async () => {
      if (stop || Date.now() - last < 4 * 60 * 1000) return;
      last = Date.now();
      // 1) version.json comparison
      try {
        const v = await fetchVersion();
        if (v?.version && v.version !== "dev") {
          if (initialVersion === null) initialVersion = v.version;
          else if (v.version !== initialVersion) { setUpdate(v); return; }
        }
      } catch { /* file may not exist on older deploys — fall through */ }
      // 2) bundle-hash comparison (covers deploys built before version stamping)
      if (!bundle) return; // dev server
      try {
        const html = await (await fetch("/", { cache: "no-store" })).text();
        const m = html.match(/\/static\/js\/(main\.[a-z0-9]+\.js)/);
        if (m && !bundle.includes(m[1])) setUpdate((u) => u || { version: null });
      } catch { /* offline — try again on next resume */ }
    };

    check();
    const t = setInterval(check, 5 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === "visible") { last = 0; check(); } };
    document.addEventListener("visibilitychange", onVis);
    return () => { stop = true; clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  if (!update || dismissed) return null;
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[300] w-[calc(100%-2rem)] max-w-md" data-testid="app-update-banner">
      <div className="rounded-xl border-2 border-[#FF5F15] bg-[#09090B] text-white shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#FF5F15] flex items-center justify-center shrink-0">
            <Sparkles className="w-4.5 h-4.5 text-black" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-sm uppercase tracking-wider">Fieldlink just updated</div>
            <div className="text-xs text-zinc-400 mt-0.5">
              A new version{update.version ? ` (${update.version})` : ""} is live. Refresh to keep everything
              working properly — finish anything you're typing first.
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-3">
          <button type="button" onClick={() => setDismissed(true)}
            className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 hover:text-white px-3 py-2"
            data-testid="app-update-later">
            Later
          </button>
          <button type="button" onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 bg-[#FF5F15] hover:bg-[#E65100] text-black font-bold uppercase tracking-wider text-[11px] px-4 py-2 rounded-lg transition-colors"
            data-testid="app-update-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Update now
          </button>
        </div>
      </div>
    </div>
  );
}

// Small "v20260831.abc1234" tag for the sidebar so users & support can confirm
// which build someone is running when diagnosing deploy issues.
export function AppVersionTag({ className = "" }) {
  const [v, setV] = useState(null);
  useEffect(() => {
    fetchVersion().then((d) => setV(d?.version || null)).catch(() => {});
  }, []);
  if (!v || v === "dev") return null;
  return (
    <div className={`text-[9px] font-mono text-zinc-400 dark:text-zinc-600 text-center ${className}`} data-testid="app-version-tag">
      v{v}
    </div>
  );
}
